import { z } from 'zod';
import type { Valoracion } from './idoneidad.js';
import type { AccionPista } from './transcode.js';

/** Revisión de una biblioteca (D-28): lo que la API dice de cada película que ya está en
 *  Plex y lo que la página enseña. Vive aquí porque el veredicto y sus motivos los calcula
 *  el servidor y los pinta el navegador, y un nombre distinto a cada lado es una fila que
 *  sale en blanco sin que nada avise. */

export const veredictoRevisionSchema = z.enum([
  /** El fichero es lo que la app produciría, y el sitio no tiene nada mejor. */
  'bien',
  /** La versión vale pero el fichero no es directo: pasa por el conversor, sin bajar nada. */
  'convertir',
  /** HispaShare tiene una versión que gana en algo (D-21) sin perder en nada. */
  'redescargar',
  /** HispaShare tiene una versión con intercambio: mejor en una, peor en otra. */
  'valorar',
  /** El item de Plex tiene varios ficheros (versiones, CD1/CD2): no se toca a ciegas. */
  'a_mano',
  /** Sin ficha en HispaShare (sin IMDb en Plex, o no se encontró): solo se sabe si convertir. */
  'sin_ficha',
  /** La ruta no existe para nosotros. */
  'sin_fichero',
  /** ffprobe o algo más falló; el motivo va en `error`. */
  'error',
]);
export type VeredictoRevision = z.infer<typeof veredictoRevisionSchema>;

/** Una pista del fichero, resumida para la fila. */
export interface PistaRevisada {
  codec: string;
  idioma: string | null;
  forzado: boolean;
  canales: number | null;
  /** Subtítulo de imagen (PGS, VobSub): habría que pasarlo por OCR. */
  esBitmap: boolean;
}

/** Lo que ffprobe dijo del fichero, reducido a lo que se enseña y se compara. */
export interface AnalisisRevisado {
  container: string | null;
  videoCodec: string | null;
  ancho: number | null;
  alto: number | null;
  hdr: string | null;
  bitrateMbps: number | null;
  duracionMin: number | null;
  audio: PistaRevisada[];
  subtitulos: PistaRevisada[];
}

/** Lo que haría el conversor con el fichero, en el mismo vocabulario del editor de pistas. */
export interface PlanRevisado {
  video: { accion: AccionPista; motivo: string } | null;
  audio: { idioma: string | null; codec: string; accion: AccionPista; motivo: string }[];
  subtitulos: { idioma: string | null; codec: string; accion: AccionPista; motivo: string }[];
  reescalarA: number | null;
  avisos: string[];
}

/** La mejor versión que ofrece HispaShare, ya valorada con las mismas preferencias. */
export interface CandidataRevisada {
  elinkId: number;
  releaseFileId: string | null;
  resolution: string | null;
  container: string | null;
  videoCodec: string | null;
  audioLangs: string[];
  subtitleLangs: string[];
  sizeBytes: number | null;
  downloadCount: number | null;
  valoracion: Valoracion;
  /** Por qué gana o por qué es un intercambio, en columnas de la valoración. */
  mejora: string[];
  empeora: string[];
}

export interface FilaRevision {
  id: string;
  libraryId: string;
  ratingKey: string;
  filePath: string;
  sizeBytes: number | null;
  title: string;
  year: number | null;
  imdbId: string | null;
  movieId: string | null;
  hispashareId: number | null;
  kind: 'movie' | 'show';
  veredicto: VeredictoRevision;
  /** Una frase por decisión: por qué convertir, por qué gana el sitio, qué falta. */
  motivos: string[];
  valoracion: Valoracion | null;
  analisis: AnalisisRevisado | null;
  plan: PlanRevisado | null;
  mejor: CandidataRevisada | null;
  /** La descarga o conversión que ya se pidió desde aquí, si hay. */
  downloadId: string | null;
  downloadStatus: string | null;
  /** Cuánto ocupan las `Plex Versions` que cuelgan de este item. 0 si no tiene. */
  versionesPlexBytes: number;
  reviewedAt: string;
  error: string | null;
}

/** Cuántas de cada, para las fichas de filtro de la página. */
export type ResumenRevision = Record<VeredictoRevision, number>;
